import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

/**
 * Action buttons for downloading images, sharing links, and clearing the chart.
 * @fires download-png - When the PNG download button is clicked.
 * @fires download-svg - When the SVG download button is clicked.
 * @fires copy-link - When the "Copy shareable link" button is clicked.
 * @fires clear-requested - When the "Clear" button is clicked.
 */
@customElement("bab-share-row")
export class BabShareRow extends LitElement {
  static styles = css`
    :host {
      margin-top: 20px;
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      animation: banner-in 0.2s ease;
    }

    .share-btn {
      padding: 9px 20px;
      background: var(--bab-accent, #4a90e2);
      color: #fff;
      border: var(--bab-border-width, 1.5px) solid var(--bab-border);
      border-radius: var(--bab-radius, 12px);
      font-size: 0.9rem;
      font-weight: var(--bab-font-weight, 600);
      font-family: inherit;
      cursor: pointer;
      transition: opacity 0.15s, transform 0.1s;
    }

    .share-btn:hover {
      opacity: 0.88;
    }

    .share-btn:active {
      transform: scale(0.97);
    }

    .share-btn:focus-visible {
      outline: 2px solid var(--bab-accent, #4a90e2);
      outline-offset: 2px;
    }

    .share-btn--secondary {
      background: var(--bab-button-secondary-bg, #fff);
      color: var(--bab-button-secondary-text, #4a90e2);
      padding: 7.5px 18.5px;
    }

    .share-btn--secondary:hover {
      background: var(--bab-bg, #f0f4f8);
      opacity: 1;
    }

    .share-btn--warn {
      background: #f59e0b;
      border-color: #f59e0b;
      color: #fff;
    }

    @keyframes banner-in {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: translateY(0); }
    }
  `;

  @property({ type: Boolean })
  locked = false;

  @property({ type: Boolean })
  clearPending = false;

  render() {
    return html`
      <button class="share-btn share-btn--secondary" @click="${() => this.dispatchEvent(new CustomEvent("download-png"))}">
        Download PNG
      </button>
      <button class="share-btn share-btn--secondary" @click="${() => this.dispatchEvent(new CustomEvent("download-svg"))}">
        Download SVG
      </button>
      ${!this.locked
        ? html`
            <button class="share-btn" @click="${() => this.dispatchEvent(new CustomEvent("copy-link"))}">
              Copy shareable link
            </button>
            <button
              class="share-btn share-btn--secondary ${this.clearPending ? "share-btn--warn" : ""}"
              @click="${() => this.dispatchEvent(new CustomEvent("clear-requested"))}"
            >
              ${this.clearPending ? "Confirm clear?" : "Clear"}
            </button>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bab-share-row": BabShareRow;
  }
}
